// Which filter buckets an item belongs to — set at fetch time by the composable.
// An item may belong to multiple buckets (e.g. linked to both a group event and
// a standalone event → ['groups', 'events']). 'unlinked' is always exclusive.
export type LinkType = 'groups' | 'events' | 'unlinked'

// Base calendar item interface that all specific types extend
export interface CalendarItem {
  id: number | string
  name: string
  datetime: string
  endDatetime?: string // For multi-day items
  href?: string
  image?: string // Optional image URL (primarily for events)
  eventOid?: number // OID of the linked event (for data capture campaigns)
  linkTypes?: LinkType[]
}

// Event type - represents actual events
export interface CalendarEvent extends CalendarItem {
  type: 'event'
  time?: string
  // Event-group membership — drives the planner's composite-group view.
  // When present, the calendar can collapse all events sharing a groupOid
  // into a single composite bar labelled with groupName.
  groupOid?: number
  groupName?: string
}

// Email campaign type
export interface CalendarEmail extends CalendarItem {
  type: 'email'
  draft: boolean
  scheduledTime?: string
}

// SMS campaign type
export interface CalendarSMS extends CalendarItem {
  type: 'sms'
  draft: boolean
  scheduledTime?: string
}

// Presale type
export interface CalendarPresale extends CalendarItem {
  type: 'presale'
  draft: boolean
  startDatetime?: string
  endDatetime: string
}

// Signup type
export interface CalendarSignup extends CalendarItem {
  type: 'signup'
  draft: boolean
  openDatetime?: string
  closeDatetime?: string
}

// Waitlist type
export interface CalendarWaitlist extends CalendarItem {
  type: 'waitlist'
  draft: boolean
  openDatetime?: string
  closeDatetime?: string
}

// Competition type
export interface CalendarCompetition extends CalendarItem {
  type: 'competition'
  draft: boolean
  startDatetime?: string
  endDatetime: string
}

// Custom item type
export interface CalendarCustomItem extends CalendarItem {
  type: 'customItem'
  draft: boolean
  description?: string
  color?: string
  eventOids?: number[]
}

// Calendar item types the planner filter exposes as checkboxes.
export type CalendarItemType = AnyCalendarItem['type']

export const ALL_CALENDAR_ITEM_TYPES: CalendarItemType[] = [
  'event', 'email', 'sms', 'presale', 'signup', 'waitlist', 'competition', 'customItem',
]

// Filter checkboxes are grouped into colour-coded categories (one or more
// item types each), styled to match how the items render on the calendar.
export interface CalendarItemCategory {
  key: string
  label: string
  icon: string
  types: CalendarItemType[]
  bgClass: string
  textClass: string
}

export const CALENDAR_ITEM_CATEGORIES: CalendarItemCategory[] = [
  { key: 'event', label: 'Events', icon: 'calendar', types: ['event'], bgClass: 'bg-surface', textClass: 'text-primary' },
  { key: 'email', label: 'Email messages', icon: 'email', types: ['email'], bgClass: 'bg-calendar-email', textClass: 'text-font-calendar-email' },
  { key: 'sms', label: 'SMS messages', icon: 'sms', types: ['sms'], bgClass: 'bg-calendar-sms', textClass: 'text-font-calendar-sms' },
  { key: 'dataCapture', label: 'Data capture', icon: 'target', types: ['presale', 'signup', 'waitlist', 'competition'], bgClass: 'bg-calendar-campaign', textClass: 'text-font-calendar-campaign' },
  { key: 'customItem', label: 'Custom calendar items', icon: 'calendar', types: ['customItem'], bgClass: 'bg-surfaces-neutral', textClass: 'text-primary' },
]

// Whether events are shown one-per-event or collapsed into their event group.
export type EventGroupingMode = 'individual' | 'composite'

export interface MarketingPlannerFilter {
  eventsQuery: string
  venuesQuery: string
  labelsQuery: string
  startDate: string
  endDate: string
  itemTypes: CalendarItemType[]
  eventGrouping: EventGroupingMode
}

export const createDefaultMarketingPlannerFilter = (): MarketingPlannerFilter => ({
  eventsQuery: '',
  venuesQuery: '',
  labelsQuery: '',
  startDate: '',
  endDate: '',
  itemTypes: [...ALL_CALENDAR_ITEM_TYPES],
  eventGrouping: 'individual',
})

// Union type of all calendar item types
export type AnyCalendarItem =
  | CalendarEvent
  | CalendarEmail
  | CalendarSMS
  | CalendarPresale
  | CalendarSignup
  | CalendarWaitlist
  | CalendarCompetition
  | CalendarCustomItem

// Helper type to check if an item has a draft status
export type DraftableCalendarItem = Exclude<AnyCalendarItem, CalendarEvent>

// Color scheme definitions for each item type
export const CALENDAR_ITEM_COLORS = {
  event: {
    bg: 'bg-black',
    text: 'text-inverse',
    border: 'border-brand',
    draftText: 'text-brand',
    surface: 'bg-calendar-event',
    // Note: Event color is typically defined by event images/branding
  },
  email: {
    bg: 'bg-calendar-email',
    text: 'text-font-calendar-email',
    border: 'border-brand',
    draftText: 'text-brand',
    surface: 'bg-calendar-email',
  },
  sms: {
    bg: 'bg-calendar-sms',
    text: 'text-font-calendar-sms',
    border: 'border-font-calendar-sms',
    draftText: 'text-font-calendar-sms',
    surface: 'bg-calendar-sms',
  },
  presale: {
    bg: 'bg-calendar-campaign',
    text: 'text-font-calendar-campaign',
    border: 'border-font-calendar-campaign',
    draftText: 'text-font-calendar-campaign',
    surface: 'bg-calendar-campaign',
  },
  signup: {
    bg: 'bg-calendar-campaign',
    text: 'text-font-calendar-campaign',
    border: 'border-font-calendar-campaign',
    draftText: 'text-font-calendar-campaign',
    surface: 'bg-calendar-campaign',
  },
  waitlist: {
    bg: 'bg-calendar-campaign',
    text: 'text-font-calendar-campaign',
    border: 'border-font-calendar-campaign',
    draftText: 'text-font-calendar-campaign',
    surface: 'bg-calendar-campaign',
  },
  competition: {
    bg: 'bg-calendar-campaign',
    text: 'text-font-calendar-campaign',
    border: 'border-font-calendar-campaign',
    draftText: 'text-font-calendar-campaign',
    surface: 'bg-calendar-campaign',
  },
  customItem: {
    bg: 'bg-calendar-custom',
    text: 'text-inverse',
    border: 'border-calendar-custom',
    draftText: 'text-calendar-custom',
    surface: 'bg-calendar-custom',
    // Note: Custom item color is defined by user in UI
  },
} as const

// Draft styling modifier
export const DRAFT_OPACITY = 'opacity-60'

// Type guard functions
export function isCalendarEvent(item: AnyCalendarItem): item is CalendarEvent {
  return item.type === 'event'
}

export function isCalendarEmail(item: AnyCalendarItem): item is CalendarEmail {
  return item.type === 'email'
}

export function isCalendarSMS(item: AnyCalendarItem): item is CalendarSMS {
  return item.type === 'sms'
}

export function isCalendarPresale(item: AnyCalendarItem): item is CalendarPresale {
  return item.type === 'presale'
}

export function isCalendarSignup(item: AnyCalendarItem): item is CalendarSignup {
  return item.type === 'signup'
}

export function isCalendarWaitlist(item: AnyCalendarItem): item is CalendarWaitlist {
  return item.type === 'waitlist'
}

export function isCalendarCompetition(item: AnyCalendarItem): item is CalendarCompetition {
  return item.type === 'competition'
}

export function isCalendarCustomItem(item: AnyCalendarItem): item is CalendarCustomItem {
  return item.type === 'customItem'
}

export function hasDraft(item: AnyCalendarItem): item is DraftableCalendarItem {
  return !isCalendarEvent(item)
}
